// src/components/layouts/BoxedLayout.tsx
'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { getMenuOptions } from '@/components/menu';

export default function BoxedLayout({ children, title, userName, userRole, userAvatar, userInitials, handleLogout, config, API_URL }: any) {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const isActive = (path: string) => pathname === path;
  const menuConfig = getMenuOptions(userRole);

  const logoUrl = config?.logo_url ? (config.logo_url.startsWith('http') ? config.logo_url : `${API_URL}${config.logo_url}`) : null;
  const companyName = config?.company_name || "Cash Money";

  return (
    <div className="min-h-screen bg-[#E5E7EB] flex items-center justify-center lg:p-6 font-sans">
      
      {/* CONTENEDOR MAESTRO (Caja flotante en Desktop, Full-screen en Móvil) */}
      <div className="bg-white w-full max-w-[1400px] lg:h-[90vh] lg:rounded-3xl lg:shadow-2xl flex flex-col overflow-hidden relative min-h-screen lg:min-h-0 border border-gray-200 lg:border-none">
        
        {/* TOP NAVBAR */}
        <header className="h-16 lg:h-20 px-4 lg:px-8 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
             {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="h-8 object-contain" />
             ) : (
                <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                  {companyName.charAt(0)}
                </div>
             )}
             <span className="font-bold text-lg tracking-tight truncate max-w-[130px]" title={companyName}>{companyName}</span>
          </div>

          {/* Menú Desktop (Centrado) */}
          <nav className="hidden lg:flex items-center gap-6">
            {menuConfig.map((group) => 
              group.items.map((item: any, i: number) => (
                <Link key={i} href={item.path} className={`flex items-center gap-2 text-sm font-semibold transition-colors pb-1 border-b-2 ${isActive(item.path) ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent hover:text-gray-700'}`}>
                  {item.name}
                </Link>
              ))
            )}
          </nav>

          <div className="flex items-center gap-4">
             {/* Botón Menú Móvil */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} /></svg>
            </button>

            {/* Perfil */}
            <div className="relative hidden sm:block">
              <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-700">{userName}</p>
                  <p className="text-xs text-gray-400 uppercase tracking-widest">{userRole}</p>
                </div>
                {userAvatar ? (
                   <img src={userAvatar} alt={userName} className="w-10 h-10 rounded-full border border-gray-200 shadow-sm object-cover" />
                ) : (
                   <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold flex items-center justify-center text-sm border border-blue-200 shadow-sm">
                     {userInitials}
                   </div>
                )}
              </button>
              {isProfileMenuOpen && (
                <div className="absolute right-0 mt-3 w-48 bg-white border border-gray-100 rounded-xl shadow-xl py-2 z-50">
                  <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 font-medium">Cerrar Sesión</button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* MENÚ MÓVIL DESPLEGABLE */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-b border-gray-100 absolute top-16 left-0 w-full z-40 shadow-lg">
            <nav className="p-4 space-y-2">
              {menuConfig.map((group) => 
                group.items.map((item: any, i: number) => (
                  <Link key={i} href={item.path} onClick={() => setIsMobileMenuOpen(false)} className={`block px-4 py-3 rounded-xl text-sm font-medium ${isActive(item.path) ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {item.icon} {item.name}
                  </Link>
                ))
              )}
              <div className="border-t border-gray-100 pt-2 mt-2">
                <button onClick={handleLogout} className="w-full text-left px-4 py-3 text-sm text-red-600 font-medium">🚪 Cerrar Sesión</button>
              </div>
            </nav>
          </div>
        )}

        {/* ÁREA DE CONTENIDO (Layout con Título flotante) */}
        <div className="flex-1 flex flex-col overflow-hidden bg-[#F8F9FA]">
          <div className="px-4 lg:px-8 py-4 lg:py-6 flex items-center justify-between">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-800">{title}</h2>
          </div>
          
          <main className="flex-1 overflow-auto px-4 lg:px-8 pb-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 lg:p-6 min-h-full">
              {children}
            </div>
          </main>
        </div>

      </div>
    </div>
  );
}